/**
 * 消息列表（任务 9e-1，两端共用）：GlobalState.messages.timeline 只读渲染。
 * 数据写路径全在 messagesState（open/append/invalidate/older），本组件只有视图态
 * （follow / 新消息胶囊计数）；行模型来自 buildTimelineItems（9d），尺寸/计数
 * 口径在 messageListModel.ts。VirtualList 负责不定高虚拟化+粘底+触顶加载。
 *
 * - 调用方（ChatPage）按 conversationKey 加 key remount 本组件：virtualizer 的
 *   anchor/测高缓存天然按会话隔离，切会话回到粘底初始态，无需清理逻辑。
 * - timeline.key 与 props 不一致的瞬间（nav 刚变、切片未跟上）按加载态渲染。
 * - 触顶直接调 messagesConnection().loadOlder()：在途/到头/未回包在 connection
 *   内 no-op，组件不重复判；items 不变不会再触发（VirtualList 门控）。
 * - 新消息胶囊：非 follow 时统计新 append 的对方内容行；回到底部（手动滚回或
 *   点按钮）清零。首屏整批不计（lastMaxId 从首个非空 rows 初始化）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { VirtualList, type VirtualListApi } from '~/list/VirtualList';
import { useGlobal } from '~/store/useGlobal';
import { markOnce, PERF_MARKS } from '~/perf/marks';
import { endInteraction, INTERACTIONS } from '~/perf/interaction';
import { appWorker, contactsConnection, messagesConnection } from '~/store/appWorker';
import { sendStatusOf, type SendStatus } from '~/chat/readState';
import { ChatSkeleton } from '~/ui/Skeleton';
import { useDelayedFlag } from './useDelayedFlag';
import { registerAnchorSource, takeRestoredAnchor } from '~/store/restoreState';
import {
  albumTileToItem,
  buildTimelineItems,
  parseMessageContent,
  replyThumbPath,
  type MessageContent,
  type MessageItemVM,
  type SystemItemVM,
  type TimelineItemVM,
} from '~/chat/messageModel';
import { computeAlbumLayout } from '~/chat/albumModel';
import { canForwardMessage } from '~/chat/forwardModel';
import type { ConversationKey } from '~/worker/ws/messages';
import type { ReplyRef } from '~/chat/replyModel';
import { formatDateSeparator } from '~/chat/formatTime';
import { resolveMediaUrl } from '~/chat/mediaUrl';
import { systemNoteText } from '~/chat/preview';
import { parseRichText } from '~/chat/richText';
import { appRouter } from '~/nav/appRouter';
import { openFriendDetail } from '~/nav/actions';
import { avatarStyle } from '~/chat/avatarColor';
import { premiumNoBadge, senderRoleBadge, userVipBadge } from '~/chat/vipBadge';
import { buildSenderNameMap } from '~/chat/groupMemberName';
import type { GroupMember } from '~/worker/contract';
import { formatVoiceDuration } from '~/chat/formatTime';
import {
  countNewIncoming,
  FILE_CARD_WIDTH,
  formatFileSize,
  hasNewMention,
  imageBoxSize,
  IMAGE_MAX_W,
  latestMentionId,
  stickerBoxSize,
  VOICE_BAR_COUNT,
  voiceBarHeight,
} from './messageListModel';
import { PremiumBadge, RoleBadge, VipBadge } from './VipBadge';
import { VideoOverlay } from './VideoOverlay';
import { useVoiceEntryState, voicePlayer } from './voicePlayer';
import { useNowMinute } from './useNowMinute';
import { useLongPress } from './useLongPress';
import { MessageMenuLazy } from './MessageMenuLazy';
import { ConfirmDialog } from './ConfirmDialog';
import { ForwardPickerLazy } from './ForwardPickerLazy';
import { readyMenuActions, type MessageMenuActionId } from './messageMenuModel';
import {
  isItemSelected,
  selectableIdsOf,
  selectAllLoaded,
  exceedsForwardLimit,
  FORWARD_LIMIT_TEXT,
  SELECTION_LIMIT_TEXT,
  toggleSelection,
} from './selectionModel';
import { ImageViewerLazy } from './ImageViewerLazy';
import { StickerPackPreview } from './StickerPackPreview';
import { PinBanner } from './PinBanner';
import { JumpMissingDialog, type JumpMissingSource } from './JumpMissingDialog';
import { NoticeBanner } from './NoticeBanner';
import { GroupNoticeDialogLazy } from './GroupNoticeDialogLazy';
import { MemberCardSheetLazy } from './MemberCardSheetLazy';
import { shouldShowNoticeBanner } from './groupNoticeModel';
import { copyText } from '~/util/clipboard';
import { saveImage } from '~/util/saveAs';
import styles from './messageList.module.scss';
/**
 * 超过几个目标才画进度条（21u，人工：「如果超过 5 个人要加上进度显示」）。
 * 阈值以下总共也就两三帧、几百毫秒，进度条闪一下反而像故障。
 */
const FORWARD_PROGRESS_MIN_TARGETS = 5;
import { IconCheck, IconCheckAll, IconClock, IconClose, IconFlame, IconLock } from '~/ui/icons';

/** 文本气泡单行量级的估高；图片行首测后进缓存，估偏由 reconcile 补偿 */
const ESTIMATE_ROW_HEIGHT = 48;

/** 跳转失败弹窗的空快照（没带快照的入口，如被@跳转：弹窗只显降级原因一行） */
const EMPTY_JUMP_SOURCE: JumpMissingSource = { name: '', msgType: 0, content: '', text: '' };

const itemKeyOf = (it: TimelineItemVM) => it.key;

/**
 * 本地图片预览 URL（12b）：uuid→blob URL，模块级**累积不清**——outbox 条目退场
 * 后回显的服务端行（同 uuid）继续用本地预览，避开远端 URL 的 Content-Type 矫正
 * 窗口期（protocol §10.1）。blob URL 本身活到页面卸载（session 只在登出时回收），
 * uuid 全局唯一无会话串扰；写入幂等，render 期执行安全。
 */
const localPreviewUrls = new Map<string, string>();

export function MessageList({
  conversationKey,
  onReply,
  onEdit,
  onSelectingChange,
}: {
  conversationKey: string;
  /** 菜单「回复」：把被引用条目交给 ChatPage（Composer 预览条，14.6） */
  onReply?: (item: MessageItemVM) => void;
  /** 菜单「编辑」：把目标条目交给 ChatPage（Composer 编辑条，14.7b） */
  onEdit?: (item: MessageItemVM) => void;
  /** 多选态进出（14.9）：ChatPage 据此隐藏输入栏（底部批量工具栏顶位）并清引用/编辑态 */
  onSelectingChange?: (selecting: boolean) => void;
}) {
  const timeline = useGlobal((s) =>
    s.messages.timeline?.key === conversationKey ? s.messages.timeline : null,
  );
  const myUid = useGlobal((s) => (s.auth.status === 'logged-in' ? s.auth.userId : 0));
  const cdn = useGlobal((s) => s.config?.cdn ?? '');
  const nowMs = useNowMinute();
  const isGroup = conversationKey.startsWith('group:');

  /**
   * 群聊发送者显示名（21am-7，人工报「群里客人备注没有了」）：消息行里的昵称是
   * 发送时点的原始昵称快照、**不含群备注**（groupMemberName 文件头有后端实锤），
   * 所以按成员列表 + 通讯录备注现算一张覆盖表。
   *
   * 成本：每个群每个 WS 会话期 **1 次** list_member（PageSize 500，Worker 按
   * membersCache 缓存，切回来零 HTTP；2000 人群 4 次）。旧版同样是进群聊就拉
   * （chat_screen._loadGroupSenderNames），这笔是对齐旧版表现的必要开销。
   * 失败/未回包时表为空 → 退化成显示行里的昵称快照，不留空名、不阻塞渲染。
   */
  const [members, setMembers] = useState<readonly GroupMember[]>([]);
  /**
   * ⚠️ 必须等时间线切片建好再拉（21am-9 修）：`loadGroupMembers()` 是拿
   * `store.messages.timeline` 现读的，切片还没建就**直接返回 null**
   * （messagesState 口径），而本 effect 的依赖里没有别的东西会变 → 永不重试，
   * 成员表一直是空的，备注自然套不上（人工报「好友备注在群里还是看不到」）。
   * ChatPage 的 loadComposer/loadGroupProfile 同样用 timelineReady 把着这道门。
   * 用**布尔量**而不是 timeline 本身做依赖：后者每来一条消息就换引用，会把
   * 成员列表反复重拉。
   */
  const timelineReady = timeline !== null;
  useEffect(() => {
    setMembers([]);
    if (!isGroup || !timelineReady) return;
    let alive = true;
    void messagesConnection()
      .loadGroupMembers()
      .then((list) => {
        if (alive && list !== null) setMembers(list);
      });
    return () => {
      alive = false;
    };
  }, [conversationKey, isGroup, timelineReady]);
  // 订阅册引用而非在选择器里遍历（册几百人时不该每帧线性扫，ChatSubtitle 同口径）
  const contactsBook = useGlobal((s) => s.contacts.book);
  /**
   * 群聊要用到「我给好友的备注」那一级，所以**进群聊就得保证通讯录册在手**
   * （21am-9，人工报「我给好友的备注在群里还是看不到」）。
   *
   * 册此前只有通讯录页会加载（ContactsPage 的 useNullThenLoad），没进过那一页时
   * `book` 恒 null → friendNoteMap 空 → 备注那一级永远落空，只剩群备注/昵称。
   * 旧版同样是进群聊就显式拉一次好友列表（chat_screen._loadGroupSenderNames 里的
   * `FriendApiService.fetchList()`），这里对齐。
   *
   * 成本：**每会话至多 1 次** with_friends——Worker 侧 contactsBook 有会话期缓存，
   * 之后进通讯录页反而是缓存命中零 HTTP（这笔请求只是提前付了）。ref 兜住重复触发：
   * 册到位前切几次群聊不该各发一次 RPC。
   */
  const bookRequestedRef = useRef(false);
  useEffect(() => {
    if (!isGroup || contactsBook !== null || bookRequestedRef.current) return;
    bookRequestedRef.current = true;
    void contactsConnection().loadBook();
  }, [isGroup, contactsBook]);
  const senderNames = useMemo(
    () => (isGroup ? buildSenderNameMap(members, contactsBook, myUid) : null),
    [isGroup, members, contactsBook, myUid],
  );

  const isMobile = useGlobal((s) => s.layout.isMobile);
  const isStandalone = useGlobal((s) => s.layout.isStandalone);
  // H5/standalone 保存优先 navigator.share（PROMPT 硬口径：standalone 下载体验差）
  const preferShare = isMobile || isStandalone;

  const apiRef = useRef<VirtualListApi | null>(null);
  const followRef = useRef(true);
  const [follow, setFollow] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const lastMaxIdRef = useRef(0);
  /** 发消息反馈打点（20c）：上一次渲染时的 outbox 条目数，变多 = 乐观气泡刚进 items */
  const outboxCountRef = useRef(0);
  /** 操作菜单：记行 key 不记条目——条目随 store 变（重试转 pending、删除退场）要跟上 */
  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null);
  /** 删除强确认（11c 单条 / 14.9 批量同路）：记服务端消息 id 组；busy=HTTP 在途、error=失败留对话框重试 */
  const [confirmDelete, setConfirmDelete] = useState<{
    ids: readonly number[];
    busy: boolean;
    error: string;
    /**
     * 可管理的目标发送者（21j-1，旧版 _deleteMessage 的 canModerateSender）：
     * 群聊 + 我是管理员/群主 + 不是我自己的消息 + 有发送者 id 时才有值，
     * 对话框据此多出「删该用户全部」「删全部并踢出拉黑」两个动作。
     */
    moderate?: { userId: number; name: string };
  } | null>(null);
  /** 转发（14.8b 单条 / 14.9 批量同路）：记源消息 id 组；busy=N×200ms 串行发帧在途（选择器禁交互） */
  const [forwardIds, setForwardIds] = useState<readonly number[] | null>(null);
  const [forwardBusy, setForwardBusy] = useState(false);
  // 对方已读到哪条（21v）：会话行的 peerLastReadId，由 WS 已读回执推进
  const peerReadId = useGlobal(
    (st) => st.conversations.rows?.find((r) => r.key === conversationKey)?.remote.peerLastReadId ?? 0,
  );
  /** 串行发帧进度（21u）；undefined=不画进度条（目标数未超阈值或未在发送） */
  const [forwardProgress, setForwardProgress] = useState<{ sent: number; total: number }>();
  const [forwardToast, setForwardToast] = useState('');
  const forwardToastTimerRef = useRef(0);
  /** 全屏预览（12c）：记展示 URL（localUrl 优先，已解析），null=关闭 */
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  /** 贴纸包预览（14.17c）：点贴纸气泡（pack_id>0）记包 id，null=关闭 */
  const [previewPackId, setPreviewPackId] = useState<number | null>(null);
  /** 包预览加/移成功提示（弹层已关，提示要活得比弹层久——forwardToast 同策） */
  const [packToast, setPackToast] = useState('');
  const packToastTimerRef = useRef(0);
  /** 视频播放浮层（14.3）：记已解析 URL，null=关闭 */
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  /** 引用跳转闪烁高亮（14.6b）：记目标行 key，900ms 后清（旧版 _flashMessage 同口径） */
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const flashTimerRef = useRef(0);
  /**
   * 引用/置顶目标到头、封顶、已删的降级（21ah 改 toast 为弹窗）：直接把点击方
   * 手上的原消息快照摊开展示（JumpMissingDialog），null=关。
   */
  const [jumpMissing, setJumpMissing] = useState<JumpMissingSource | null>(null);
  /** 群公告弹窗（21al）：true=开。内容取 timeline.composer.notice，零请求 */
  const [noticeOpen, setNoticeOpen] = useState(false);
  /** 点头像出的成员卡（21an）：null=关；昵称是成员表没到时的兜底标题 */
  const [memberCard, setMemberCard] = useState<{ userId: number; name: string } | null>(null);
  const onAvatarTap = useCallback((userId: number, nickname: string) => {
    if (userId > 0) setMemberCard({ userId, name: nickname });
  }, []);
  /** 14.6c：补拉完成后的跳转意图（rows 合并后 items 重建，effect 消费一次） */
  const [pendingJump, setPendingJump] = useState<number | null>(null);
  const jumpSeqRef = useRef(0);
  /** 本次跳转意图随手带的快照（补拉失败/空隙降级时弹窗展示，无=空快照） */
  const jumpSourceRef = useRef<JumpMissingSource>(EMPTY_JUMP_SOURCE);
  /**
   * 多选（14.9）：null=非多选；集合=已选消息 id（相册整册进退，selectionModel）。
   * 组件按会话 remount，切会话自动退出；集合清空即退出（旧版 isNotEmpty 同口径，
   * 工具栏按钮因此恒有选中项无需 disabled）。
   */
  const [selection, setSelection] = useState<ReadonlySet<number> | null>(null);
  const [limitTip, setLimitTip] = useState(false);
  const limitTipTimerRef = useRef(0);
  useEffect(
    () => () => {
      clearTimeout(flashTimerRef.current);
      clearTimeout(forwardToastTimerRef.current);
      clearTimeout(limitTipTimerRef.current);
      clearTimeout(packToastTimerRef.current);
    },
    [],
  );
  const selecting = selection !== null;
  useEffect(() => {
    onSelectingChange?.(selecting);
  }, [selecting, onSelectingChange]);
  // 进出多选是两跳布局（本提交挂/撤选择条与工具栏，ChatPage 收到回调后下一提交
  // 换 Composer）+整列重测高（勾选控件进出改行宽），跨多帧的回声滚动会把粘底
  // 误判成用户上滚（32px 阈值 < 条高）。转换前粘底的，转换后连续几帧重新贴底，
  // 吃掉迟到的布局跳变；非粘底进出（从历史位置进多选）不动视口。仅真正转换时
  // 执行——挂载首跑不算（否则 13b 冷启动恢复回放的 anchor 会被顶掉）。
  const prevSelectingRef = useRef(selecting);
  useEffect(() => {
    if (prevSelectingRef.current === selecting) return;
    prevSelectingRef.current = selecting;
    if (!followRef.current) return;
    let frames = 0;
    let rafId = requestAnimationFrame(function pin() {
      apiRef.current?.scrollToBottom();
      if (++frames < 8) rafId = requestAnimationFrame(pin);
    });
    return () => cancelAnimationFrame(rafId);
  }, [selecting]);

  const rows = timeline?.rows ?? null;
  const outbox = timeline?.outbox;
  const pins = timeline?.pins ?? null;
  // 群公告（21ah）：顺着既有的限制态通道来（timeline.composer），不另取一份
  const notice = timeline?.composer?.notice ?? '';

  // 置顶列表加载（14.10b）：每次打开会话读一次（组件按会话 remount）——Worker
  // 按 WS 会话期缓存命中零 HTTP（requests §3 注 10）；失败 pins 留 null 不渲染
  // 横幅，重开重试，不在本 mount 内循环重试。
  const pinsRequestedRef = useRef(false);
  const hasTimeline = timeline !== null;
  useEffect(() => {
    if (pinsRequestedRef.current || !hasTimeline) return;
    pinsRequestedRef.current = true;
    void messagesConnection().loadPins();
  }, [hasTimeline]);

  // 置顶操作提示：受理 false 当场提示；异步失败（error_msg→Worker 已回滚行与
  // 横幅）由 pinFailedSeq 变化提示。基线按首次观测记（timeline 开着 seq 从 0 起，
  // 组件按会话 remount 天然隔离）。
  const [pinToast, setPinToast] = useState('');
  const pinToastTimerRef = useRef(0);
  const showPinToast = useCallback((text: string) => {
    clearTimeout(pinToastTimerRef.current);
    setPinToast(text);
    pinToastTimerRef.current = window.setTimeout(() => setPinToast(''), 3000);
  }, []);
  useEffect(() => () => clearTimeout(pinToastTimerRef.current), []);
  const pinFailedSeq = timeline?.pinFailedSeq ?? 0;
  const pinFailBaseRef = useRef<number | null>(null);
  useEffect(() => {
    const base = pinFailBaseRef.current;
    pinFailBaseRef.current = pinFailedSeq;
    if (base !== null && pinFailedSeq > base) showPinToast('置顶操作失败，已还原');
  }, [pinFailedSeq, showPinToast]);

  const requestPin = useCallback(
    (msgId: number, pin: boolean) => {
      void messagesConnection()
        .pinMessage(msgId, pin)
        .then((ok) => {
          if (!ok) showPinToast('操作未发送，请检查网络后重试');
        });
    },
    [showPinToast],
  );
  const onUnpin = useCallback((msgId: number) => requestPin(msgId, false), [requestPin]);

  if (outbox !== undefined) {
    for (const e of outbox) {
      if (e.localUrl !== undefined) localPreviewUrls.set(e.clientId, e.localUrl);
    }
  }
  const encryptionOn = timeline?.encryptionOn === true;
  // 我在本群的角色（14.25a）：随 chat/composer 通道透出（打开群聊即有，零新增
  // 请求）；私聊/未回包按 0——群聊删除项在回包落定后自然出现
  const myRole = timeline?.composer?.myRole ?? 0;
  const items = useMemo(
    () =>
      rows === null
        ? []
        : buildTimelineItems(rows, {
            isGroup,
            myUid,
            encryptionOn,
            myRole,
            ...(senderNames !== null ? { senderNames } : {}),
            ...(outbox !== undefined ? { outbox } : {}),
          }),
    [rows, outbox, isGroup, myUid, encryptionOn, senderNames, myRole],
  );

  // 被@跳转钮（14.11b，仅群聊）：本地闩锁态——挂载观测到会话 isAt=1 置起（noteRead
  // 开聊即清服务端 isAt，这里对「观测到 1」闩锁），append 带 @我 的行再置起；
  // 点击即清（已读上报既有链负责清服务端态）。组件按会话 remount 天然隔离。
  const [mentionFlag, setMentionFlag] = useState(false);
  const isAtRemote = useGlobal((s) =>
    isGroup ? (s.conversations.rows?.find((r) => r.key === conversationKey)?.remote.isAt ?? 0) : 0,
  );
  useEffect(() => {
    if (isAtRemote === 1) setMentionFlag(true);
  }, [isAtRemote]);

  useEffect(() => {
    if (rows === null || rows.length === 0) return;
    const maxId = rows[rows.length - 1]!.id;
    const prev = lastMaxIdRef.current;
    lastMaxIdRef.current = Math.max(prev, maxId);
    if (prev === 0 || maxId <= prev) return; // 首屏整批 / 翻页 prepend 不计
    if (isGroup && hasNewMention(rows, prev, myUid)) setMentionFlag(true);
    if (!followRef.current) setNewCount((c) => c + countNewIncoming(rows, prev, myUid));
  }, [rows, myUid, isGroup]);

  const onFollowChange = useCallback((f: boolean) => {
    followRef.current = f;
    setFollow(f);
    if (f) setNewCount(0);
  }, []);

  // 冷启动恢复（13b）：注册 anchor 源（页面隐藏时 wakeState 取走存恢复点）；
  // 首屏 rows 就绪后回放一次暂存 anchor（粘底是默认态无需回放；anchor 行不在
  // 首页时 virtualizer 自动回粘底）。本组件按会话 remount，ref 天然按会话隔离。
  useEffect(
    () => registerAnchorSource(conversationKey, () => apiRef.current?.getAnchor() ?? { kind: 'bottom' }),
    [conversationKey],
  );
  const anchorRestoredRef = useRef(false);
  useEffect(() => {
    if (anchorRestoredRef.current || rows === null) return;
    anchorRestoredRef.current = true;
    const anchor = takeRestoredAnchor(conversationKey);
    if (anchor !== null && anchor.kind === 'item') apiRef.current?.restoreAnchor(anchor);
  }, [rows, conversationKey]);

  const onReachTop = useCallback(() => {
    messagesConnection().loadOlder();
  }, []);

  // 语音播放不跨会话续播（14.2a）：本组件按会话 remount，卸载即停
  useEffect(() => () => voicePlayer().stop(), []);

  // ---- 操作菜单（任务 11b）：桌面右键 / 移动长按，事件委托在容器上 ----
  // 相册条目（14.1）：每张 tile 自带 data-mkey，这里按张合成单图视图，菜单/
  // 预览/删除与独立图片消息同一条路径（首张 key 与条目 key 相同，tile 视图
  // 后写入覆盖条目——点中 tile 永远按张操作）
  const itemByKey = useMemo(() => {
    const map = new Map<string, MessageItemVM>();
    for (const it of items) {
      if (it.type !== 'message') continue;
      map.set(it.key, it);
      if (it.content.kind === 'album') {
        for (const img of it.content.images) map.set(img.key, albumTileToItem(it, img));
      }
    }
    return map;
  }, [items]);

  const openMenuAt = useCallback(
    (target: Element, x: number, y: number) => {
      if (selecting) return; // 多选态长按/右键不出菜单（点击=切换选中）
      const key = target.closest('[data-mkey]')?.getAttribute('data-mkey');
      if (key != null) setMenu({ key, x, y });
    },
    [selecting],
  );

  // 多选按整行（相册 tile 归并到合并条目，与引用跳转 findRowKey 同口径）：
  // itemByKey 里首张 tile 覆盖了条目 key，这里要的是行级条目，单独线性找
  const rowItemOfKey = useCallback(
    (key: string): MessageItemVM | undefined => {
      for (const it of items) {
        if (it.type !== 'message') continue;
        if (
          it.key === key ||
          (it.content.kind === 'album' && it.content.images.some((img) => img.key === key))
        ) {
          return it;
        }
      }
      return undefined;
    },
    [items],
  );

  const showLimitTip = useCallback(() => {
    clearTimeout(limitTipTimerRef.current);
    setLimitTip(true);
    limitTipTimerRef.current = window.setTimeout(() => setLimitTip(false), 3000);
  }, []);

  const toggleRowSelection = useCallback(
    (key: string) => {
      if (selection === null) return;
      const it = rowItemOfKey(key);
      if (it === undefined) return;
      const ids = selectableIdsOf(it);
      if (ids.length === 0) return; // 不可选行点击 no-op（控件置灰）
      const { next, overflow } = toggleSelection(selection, ids);
      if (overflow) showLimitTip();
      else setSelection(next.size === 0 ? null : next); // 清空即退出（旧版口径）
    },
    [selection, rowItemOfKey, showLimitTip],
  );

  const longPressRef = useLongPress(isMobile, (x, y, target) => openMenuAt(target, x, y));

  // 菜单渲染时从当前 items 取条目与动作：条目退场/状态变化（重试转 pending）
  // 自动关闭或刷新动作集，不会操作到已过期的行
  const menuItem = menu !== null ? itemByKey.get(menu.key) : undefined;
  const menuActions =
    menuItem !== undefined
      ? readyMenuActions({
          outgoing: menuItem.outgoing,
          contentKind: menuItem.content.kind,
          isGroup,
          myRole, // 群聊管理员/群主出删除项（11a 规则；成员连自己的都不能删，func_chat.go 实锤）
          pinned: menuItem.pinned,
          secret: menuItem.secret, // 14.13c：即焚消息不出转发/选择
          encrypted: menuItem.encrypted, // 14.14b：加密消息不出编辑/转发/选择
          ...(menuItem.sendState !== undefined ? { sendState: menuItem.sendState } : {}),
        })
      : [];

  // 保存（12c 图片 / 14.3 文件·视频共用）：失败出 3s 提示条；文件/视频带
  // envelope 原始文件名（saveAs.ts 口径——有名字就不走 URL+MIME 推导）
  const saveMedia = useCallback(
    (url: string, name?: string) => {
      void saveImage(url, preferShare, name).then((r) => {
        if (r === 'failed') {
          setSaveFailed(true);
          setTimeout(() => setSaveFailed(false), 3000);
        }
      });
    },
    [preferShare],
  );

  const onMenuAction = useCallback(
    (id: MessageMenuActionId) => {
      setMenu(null);
      const it = menu !== null ? itemByKey.get(menu.key) : undefined;
      if (it === undefined) return;
      if (id === 'reply') onReply?.(it);
      // 编辑需服务端 id（回显前的乐观行点编辑 no-op，delete 同口径）
      else if (id === 'edit' && it.content.kind === 'text' && it.id > 0) onEdit?.(it);
      else if (id === 'copy' && it.content.kind === 'text') void copyText(it.content.text);
      else if (id === 'retry') messagesConnection().retrySend(it.uuid);
      else if (id === 'discard') messagesConnection().discardSend(it.uuid);
      else if (id === 'delete' && it.id > 0)
        setConfirmDelete({
          ids: [it.id],
          busy: false,
          error: '',
          // 旧版 canModerateSender 同口径（chat_screen.dart:11632）
          ...(isGroup && myRole > 0 && !it.outgoing && it.senderId > 0
            ? { moderate: { userId: it.senderId, name: it.nickname } }
            : {}),
        });
      // 转发需服务端 id（Worker 侧 canForwardMessage 兜底，这里同口径提前挡）
      else if (id === 'forward' && canForwardMessage(it.id, it.msgType, it.secret, it.encrypted))
        setForwardIds([it.id]);
      // 置顶/取消置顶（14.10）：需服务端 id（回显前乐观行 no-op，edit/delete 同口径）
      else if (id === 'pin' && it.id > 0) requestPin(it.id, !it.pinned);
      else if (id === 'select') {
        // 进多选并选中触发行（旧版 _enterMessageSelectionMode 同口径）；
        // 相册 tile 归并整册（menu.key 可能是 tile key，按行级条目取 ids）
        const row = menu !== null ? rowItemOfKey(menu.key) : undefined;
        const ids = row !== undefined ? selectableIdsOf(row) : [];
        if (ids.length > 0) setSelection(new Set(ids));
      }
      else if (id === 'saveAs') {
        const c = it.content;
        if (c.kind === 'image' || c.kind === 'file' || c.kind === 'video') {
          // 展示 URL 同 Bubble 口径：本地预览优先，避开远端矫正窗口期（12b）
          const url = localPreviewUrls.get(it.uuid) ?? resolveMediaUrl(cdn, c.url);
          if (url !== '') saveMedia(url, c.kind === 'image' ? undefined : c.fileName);
        }
      }
      // 其余动作 ready=false 不会渲染（messageMenuModel 白名单）
    },
    [menu, itemByKey, rowItemOfKey, cdn, saveMedia, onReply, onEdit, requestPin, isGroup, myRole],
  );
  const closeMenu = useCallback(() => setMenu(null), []);

  // 多选退出：桌面 Esc（legacy §6.1；移动端用选择条 ×）。转发选择器/删除确认
  // 开着时让位——它们各自处理 Esc，不连带退多选
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && forwardIds === null && confirmDelete === null) setSelection(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selecting, forwardIds, confirmDelete]);

  // 引用块点击跳转（14.6b/14.6c）：目标在已加载窗口内=滚动（已可见不动）+闪烁
  // 高亮；不在=ensureMessageLoaded 向更早方向连续补拉（本地命中零 HTTP、封顶
  // JUMP_MAX_PAGES 页；私聊群聊同策，messagesState 文件头口径）后再跳；到头/
  // 封顶/已删=toast 降级。相册内单张的目标行是合并后的 album 条目。跳近列表顶
  // 时触顶门控照常触发 loadOlder——更早历史自动补进，anchor 钉在目标行不跳。
  const findRowKey = useCallback(
    (id: number): string | null => {
      const target = `msg:${id}`;
      for (const it of items) {
        if (it.type !== 'message') continue;
        if (
          it.key === target ||
          (it.content.kind === 'album' && it.content.images.some((img) => img.key === target))
        ) {
          return it.key;
        }
      }
      return null;
    },
    [items],
  );

  const tryJump = useCallback(
    (id: number): boolean => {
      const rowKey = findRowKey(id);
      if (rowKey === null || apiRef.current?.scrollToKey(rowKey) !== true) return false;
      clearTimeout(flashTimerRef.current);
      setFlashKey(rowKey);
      flashTimerRef.current = window.setTimeout(() => setFlashKey(null), 900);
      return true;
    },
    [findRowKey],
  );

  const showJumpMissing = useCallback(() => {
    setJumpMissing(jumpSourceRef.current);
  }, []);

  const closeJumpMissing = useCallback(() => setJumpMissing(null), []);

  const onReplyJump = useCallback(
    (id: number, source?: JumpMissingSource) => {
      jumpSourceRef.current = source ?? EMPTY_JUMP_SOURCE;
      if (tryJump(id)) return;
      // 补拉期间 loadingOlder=true，既有「加载中…」条即进度提示；再次点击重新
      // 发起（seq 覆盖旧意图，ensure 本身幂等）。组件按会话 remount，切会话后
      // 旧回调随组件卸载丢弃。
      const seq = ++jumpSeqRef.current;
      setPendingJump(null);
      void messagesConnection()
        .ensureMessageLoaded(id)
        .then((r) => {
          if (seq !== jumpSeqRef.current) return;
          if (r === 'ok') setPendingJump(id);
          else showJumpMissing();
        });
    },
    [tryJump, showJumpMissing],
  );

  // ensure resolve 时 rows 已合并，这里等 items 重建后的 commit 消费跳转意图；
  // 极端空隙（行刚被 remove 推送删掉）降级 toast，不悬挂
  useEffect(() => {
    if (pendingJump === null) return;
    setPendingJump(null);
    if (!tryJump(pendingJump)) showJumpMissing();
  }, [pendingJump, tryJump, showJumpMissing]);

  // 被@钮点击（14.11b）：窗口内新→旧找 @我 的行走跳转链（已在窗口 tryJump、
  // 更早的 ensureMessageLoaded 补拉）；窗口内没有（isAt 来自更早历史）滚到底
  // 兜底（旧版同口径）。点击即清闩锁。
  const onMentionJump = useCallback(() => {
    setMentionFlag(false);
    const id = rows !== null ? latestMentionId(rows, myUid) : 0;
    if (id > 0) onReplyJump(id);
    else apiRef.current?.scrollToBottom();
  }, [rows, myUid, onReplyJump]);

  // 删除确认提交：成功后行退场走 remove 推送（store 更新），这里只管关对话框；
  // 失败留对话框显示错误允许重试（本地行未动）
  const onConfirmDelete = useCallback(() => {
    const target = confirmDelete;
    if (target === null || target.busy) return;
    setConfirmDelete({ ids: target.ids, busy: true, error: '' });
    messagesConnection()
      .deleteMessages(target.ids)
      .then(
        () => {
          setConfirmDelete(null);
          setSelection(null); // 批量删除成功退出多选（旧版同口径）；单条路径 no-op
        },
        () =>
          setConfirmDelete((c) =>
            c === null ? null : { ...c, busy: false, error: '删除失败，请检查网络后重试' },
          ),
      );
  }, [confirmDelete]);
  const onCancelDelete = useCallback(() => setConfirmDelete(null), []);

  /**
   * 删该成员全部消息（21j-1，旧版 _deleteUserGroupMessages）。kick=true 时
   * 同时踢出 + 进群黑名单。行退场走服务端 RemovedMsgIds → 本地 apply → remove
   * 推送；踢人的成员/会话清理走 29 信令，这里都不管，只负责关框与报错。
   */
  const onDeleteUserMessages = useCallback(
    (userId: number, kick: boolean) => {
      setConfirmDelete((c) => (c === null ? null : { ...c, busy: true, error: '' }));
      messagesConnection()
        .deleteUserMessages(userId, kick)
        .then((removed) => {
          if (removed === null) {
            setConfirmDelete((c) =>
              c === null ? null : { ...c, busy: false, error: '删除失败，请检查网络后重试' },
            );
            return;
          }
          setConfirmDelete(null);
          setSelection(null);
          showPinToast(kick ? '已删除全部消息并拉黑、移出群' : `已删除该用户 ${removed} 条消息`);
        });
    },
    [showPinToast],
  );

  // 转发提交（14.8b）：整批发完（N×200ms 串行，14.8a）才回——期间选择器 busy；
  // 完成即关 + toast（旧版同口径：成功「已转发」/失败 toast；新版降级载荷在
  // Worker 侧继续发不算失败，failed>0 = 有帧没发出去）
  const showForwardToast = useCallback((text: string) => {
    clearTimeout(forwardToastTimerRef.current);
    setForwardToast(text);
    forwardToastTimerRef.current = window.setTimeout(() => setForwardToast(''), 3000);
  }, []);
  const onForwardConfirm = useCallback(
    (targets: ConversationKey[]) => {
      const msgIds = forwardIds;
      if (msgIds === null || forwardBusy) return;
      setForwardBusy(true);
      // 进度（21u）：只有目标数 >5 才订阅——两三帧就结束的批次闪一下进度条
      // 反而像故障（人工的要求也正是「超过 5 个人才要进度」）。
      // 直接订阅 Worker 推送，不进 GlobalState：这是一次性的局部 UI 状态。
      let offProgress: (() => void) | undefined;
      if (targets.length > FORWARD_PROGRESS_MIN_TARGETS) {
        offProgress = appWorker().onPush('chat/forwardProgress', (p) => setForwardProgress(p));
      }
      void messagesConnection()
        .forwardMessages(msgIds, targets)
        .then((r) => {
          offProgress?.();
          setForwardProgress(undefined);
          setForwardBusy(false);
          setForwardIds(null);
          const ok = r !== null && r.failed === 0;
          if (ok) setSelection(null); // 批量转发成功退出多选（旧版同口径）；单条路径 no-op
          showForwardToast(ok ? '已转发' : '转发失败，请检查网络后重试');
        });
    },
    [forwardIds, forwardBusy, showForwardToast],
  );
  const onCloseForward = useCallback(() => setForwardIds(null), []);

  // 批量操作（14.9）：id 升序=发帧顺序/删除顺序确定（Worker 本就去重升序）
  const sortedSelection = useCallback(
    () => (selection === null ? [] : [...selection].sort((a, b) => a - b)),
    [selection],
  );
  // 选择上限 100 > 转发上限 20（21s）：超了**拦下来报**，不默默只转前 20 条
  const onBatchForward = useCallback(() => {
    const ids = sortedSelection();
    if (exceedsForwardLimit(ids.length)) {
      showForwardToast(FORWARD_LIMIT_TEXT);
      return;
    }
    setForwardIds(ids);
  }, [sortedSelection, showForwardToast]);
  const onBatchDelete = useCallback(
    () => setConfirmDelete({ ids: sortedSelection(), busy: false, error: '' }),
    [sortedSelection],
  );
  const openViewer = useCallback((url: string) => setViewerUrl(url), []);
  const closeViewer = useCallback(() => setViewerUrl(null), []);
  // 贴纸气泡点击→整包预览（14.17c，旧版 _handleStickerMessageTap 同口径）
  const openPackPreview = useCallback((packId: number) => setPreviewPackId(packId), []);
  const closePackPreview = useCallback(() => setPreviewPackId(null), []);
  const onPackResult = useCallback((text: string) => {
    clearTimeout(packToastTimerRef.current);
    setPackToast(text);
    packToastTimerRef.current = window.setTimeout(() => setPackToast(''), 3000);
  }, []);
  const showChatSkeleton = useDelayedFlag(rows === null, 150);
  const openVideo = useCallback((url: string) => setVideoUrl(url), []);
  const closeVideo = useCallback(() => setVideoUrl(null), []);

  // 时间线还没回来（21aa，人工：「点击消息如果对话列表在加载 应该要有加载动画」）。
  // ⚠️ **延迟 150ms 才出骨架**：本地已有消息时 rows 下一帧就到（零 HTTP 路径），
  // 那种情况下闪一下骨架比一直空着更难看；只有真要等网络才值得画。
  if (rows === null) {
    return showChatSkeleton ? (
      <ChatSkeleton />
    ) : (
      <div className={styles.blank} data-testid="messages-loading" />
    );
  }
  // 「可交互」打点（20a）：深链/冷启动恢复直达聊天时，首屏主内容是这里而非会话列表
  markOnce(PERF_MARKS.interactive);
  // 交互反馈打点（20c），两条都只在有在飞 span 时才产出数据（否则 no-op）：
  // - chat-rows：进/切会话的后续里程碑——目标会话的消息行（空会话=空列表）已决定。
  //   这一段比聊天页壳晚，含 Worker RPC + 读库。
  endInteraction(INTERACTIONS.chatRows, conversationKey);
  // - send-message：乐观气泡进 items 的那一帧。判据是 outbox 变长（新增本地条目），
  //   不比对 clientId——发送方不知道 Worker 生成的 id，而同一时刻只有一个在飞的发送
  //   span（beginInteraction 后发即盖）。失败态条目同样算反馈（WS 不可用时直接 failed，
  //   气泡带重试，10a）。
  const outboxCount = outbox?.length ?? 0;
  if (outboxCount > outboxCountRef.current) endInteraction(INTERACTIONS.sendMessage);
  outboxCountRef.current = outboxCount;

  // 跳转失败弹窗里的原样气泡（21ah，人工指图「名片文件这类都要原样展示」）：
  // 手上有原消息 msgType+正文（置顶行）就按真实类型走时间线同一个 Bubble——
  // 图片/贴纸画出来、语音可播、视频/文件可开、名片照常。引用 ext 只有文本快照
  // （msgType=0）→ 弹窗退回那行字。meta 传 null：弹窗里不显时间/回执。
  // 相册是时间线合并出来的形态（单条消息解析不出来），narrow 掉。
  const jumpMissingContent =
    jumpMissing !== null && jumpMissing.msgType > 0
      ? parseMessageContent(jumpMissing.msgType, jumpMissing.content)
      : null;
  const jumpMissingBubble =
    jumpMissingContent !== null && jumpMissingContent.kind !== 'album' ? (
      <Bubble
        content={jumpMissingContent}
        outgoing={false}
        meta={null}
        cdn={cdn}
        rowKey="jump-missing"
        onImageClick={openViewer}
        onStickerClick={openPackPreview}
        onVideoClick={openVideo}
        onFileSave={saveMedia}
        onReplyJump={onReplyJump}
      />
    ) : undefined;

  return (
    <div
      ref={longPressRef}
      className={isMobile ? `${styles.wrap} ${styles.noSelect}` : styles.wrap}
      data-testid="message-list"
      onContextMenu={(e) => {
        if (isMobile) return; // 长按路径；系统菜单已被 useLongPress 抑制
        if (!(e.target instanceof Element) || e.target.closest('[data-mkey]') === null) return;
        e.preventDefault();
        openMenuAt(e.target, e.clientX, e.clientY);
      }}
      // 多选态点击整行切换选中（捕获拦截，气泡内预览/播放/引用跳转不触发）；
      // 行外（滚到底按钮、工具栏、浮层）照常
      onClickCapture={
        selecting
          ? (e) => {
              if (!(e.target instanceof Element)) return;
              const row = e.target.closest('[data-testid="msg-row"]');
              if (row === null) return;
              e.preventDefault();
              e.stopPropagation();
              const key = row.getAttribute('data-mkey');
              if (key !== null) toggleRowSelection(key);
            }
          : undefined
      }
    >
      {/* 选择条/工具栏走文档流挤占列表高度（不覆盖行——粘底时最新行可见可选；
          进出多选=容器变高，VirtualList RO + 粘底保持既有路径处理） */}
      {selection !== null && (
        <div className={styles.selectionBar} data-testid="selection-bar">
          <button
            type="button"
            className={styles.selectionExit}
            data-testid="selection-exit"
            aria-label="退出选择"
            onClick={() => setSelection(null)}
          >
            <IconClose class={styles.closeGlyph} />
          </button>
          <span className={styles.selectionCount} data-testid="selection-count">
            已选择 {selection.size} 条
          </span>
          <button
            type="button"
            className={styles.selectionAll}
            data-testid="selection-all"
            onClick={() => setSelection(selectAllLoaded(items))}
          >
            全选
          </button>
        </div>
      )}
      {/* 群公告条（21ah；旧版 chat_screen 的 _buildGroupAnnouncementPinnedBar）。
          与置顶横幅分两条、公告在上，都走文档流挤占高度（覆盖层会遮粘底的最新行，
          14.9 选择条踩过）。点击**弹窗看全文**（21al 人工指图改回旧版形态：
          读一眼就关，不值得压栈一个全屏页；编辑仍在公告页，管理员从设置页进） */}
      {shouldShowNoticeBanner(conversationKey, notice) && (
        <NoticeBanner notice={notice} onOpen={() => setNoticeOpen(true)} />
      )}
      {/* 置顶横幅（14.10b）：与群公告分两条。文档流挤占
          高度（选择条同策）；跳转复用引用跳转链（ensureMessageLoaded+tryJump） */}
      {pins !== null && pins.length > 0 && (
        <PinBanner pins={pins} mobile={isMobile} onJump={onReplyJump} onUnpin={onUnpin} />
      )}
      <div className={styles.listArea}>
        <VirtualList
          items={items}
          itemKey={itemKeyOf}
          estimateHeight={ESTIMATE_ROW_HEIGHT}
          onReachTop={onReachTop}
          onFollowChange={onFollowChange}
          apiRef={apiRef}
          renderItem={(it) => (
            <TimelineRow
              item={it}
              cdn={cdn}
              nowMs={nowMs}
              isGroup={isGroup}
              peerReadId={peerReadId}
              localUrls={localPreviewUrls}
              flashKey={flashKey}
              selection={selection}
              onImageClick={openViewer}
              onStickerClick={openPackPreview}
              onVideoClick={openVideo}
              onFileSave={saveMedia}
              onReplyJump={onReplyJump}
              onAvatarTap={isGroup ? onAvatarTap : undefined}
            />
          )}
        />
        {timeline?.loadingOlder && (
          <div className={styles.loadingOlder} data-testid="loading-older">
            加载中…
          </div>
        )}
        {mentionFlag && !selecting && (
          <button
            type="button"
            className={styles.mentionJump}
            data-testid="mention-jump"
            aria-label="查看 @ 我的消息"
            onClick={onMentionJump}
          >
            @
          </button>
        )}
        {!follow && (
          <button
            type="button"
            className={newCount > 0 ? styles.newPill : styles.scrollBottom}
            data-testid="scroll-bottom"
            data-count={newCount > 0 ? newCount : undefined}
            onClick={() => {
              apiRef.current?.scrollToBottom();
              setNewCount(0);
            }}
          >
            {newCount > 0 ? `${newCount} 条新消息 ↓` : '↓'}
          </button>
        )}
      </div>
      {selection !== null && (
        <div className={styles.selectionToolbar} data-testid="selection-toolbar">
          <button
            type="button"
            className={styles.selectionForward}
            data-testid="selection-forward"
            onClick={onBatchForward}
          >
            转发
          </button>
          {/* 群聊批量删除仅管理员/群主（菜单同口径；myRole 随 composer 透出 14.25a） */}
          {(!isGroup || myRole > 0) && (
            <button
              type="button"
              className={styles.selectionDelete}
              data-testid="selection-delete"
              onClick={onBatchDelete}
            >
              删除
            </button>
          )}
        </div>
      )}
      {menu !== null && menuActions.length > 0 && (
        <MessageMenuLazy
          actions={menuActions}
          anchorX={menu.x}
          anchorY={menu.y}
          mobile={isMobile}
          onAction={onMenuAction}
          onClose={closeMenu}
        />
      )}
      {viewerUrl !== null && (
        <ImageViewerLazy
          url={viewerUrl}
          mobile={isMobile}
          preferShare={preferShare}
          onClose={closeViewer}
        />
      )}
      {videoUrl !== null && <VideoOverlay url={videoUrl} onClose={closeVideo} />}
      {previewPackId !== null && (
        <StickerPackPreview
          packId={previewPackId}
          mobile={isMobile}
          onClose={closePackPreview}
          onResult={onPackResult}
        />
      )}
      {packToast !== '' && (
        <div className={styles.saveToast} data-testid="sticker-pack-toast">
          {packToast}
        </div>
      )}
      {saveFailed && (
        <div className={styles.saveToast} data-testid="save-failed">
          保存失败，请稍后重试
        </div>
      )}
      {noticeOpen && <GroupNoticeDialogLazy notice={notice} onClose={() => setNoticeOpen(false)} />}
      {memberCard !== null && (
        <MemberCardSheetLazy
          conversationKey={conversationKey}
          userId={memberCard.userId}
          fallbackName={memberCard.name}
          onClose={() => setMemberCard(null)}
        />
      )}
      {jumpMissing !== null && (
        <JumpMissingDialog
          source={jumpMissing}
          bubble={jumpMissingBubble}
          onClose={closeJumpMissing}
        />
      )}
      {limitTip && (
        <div className={styles.saveToast} data-testid="selection-limit">
          {SELECTION_LIMIT_TEXT}
        </div>
      )}
      {pinToast !== '' && (
        <div className={styles.saveToast} data-testid="pin-toast">
          {pinToast}
        </div>
      )}
      {forwardIds !== null && (
        <ForwardPickerLazy
          title="转发消息"
          mobile={isMobile}
          busy={forwardBusy}
          progress={forwardProgress}
          onConfirm={onForwardConfirm}
          onClose={onCloseForward}
        />
      )}
      {forwardToast !== '' && (
        <div className={styles.saveToast} data-testid="forward-toast">
          {forwardToast}
        </div>
      )}
      {confirmDelete !== null && (
        <ConfirmDialog
          title="删除消息"
          message={
            confirmDelete.moderate !== undefined
              ? `选择删除方式（${confirmDelete.moderate.name !== '' ? confirmDelete.moderate.name : '该成员'}）`
              : confirmDelete.ids.length > 1
                ? `确定要删除选中的 ${confirmDelete.ids.length} 条消息吗？删除后你和对方都将无法再看到这些消息，且无法恢复。`
                : isGroup
                  ? '删除后群成员将无法再看到这条消息。'
                  : '删除后你和对方都将无法再看到这条消息，且无法恢复。'
          }
          confirmLabel={confirmDelete.moderate !== undefined ? '删除这条消息' : '删除'}
          danger
          busy={confirmDelete.busy}
          error={confirmDelete.error}
          {...(confirmDelete.moderate !== undefined
            ? {
                // 旧版 _deleteMessage 的四选项（含「取消」）：顺序与文案逐字同口径
                extraActions: [
                  {
                    id: 'user-all',
                    label: '删除该用户所有消息',
                    danger: true,
                    onClick: () => onDeleteUserMessages(confirmDelete.moderate!.userId, false),
                  },
                  {
                    id: 'user-all-kick',
                    label: '删除所有消息并拉黑、踢出群',
                    danger: true,
                    onClick: () => onDeleteUserMessages(confirmDelete.moderate!.userId, true),
                  },
                ],
              }
            : {})}
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      )}
    </div>
  );
}

function TimelineRow(props: {
  item: TimelineItemVM;
  cdn: string;
  nowMs: number;
  isGroup: boolean;
  /** 对方已读到的消息 id（会话行 peerLastReadId）；21v 的已读标记据此判定 */
  peerReadId: number;
  localUrls: ReadonlyMap<string, string>;
  flashKey: string | null;
  /** 多选态（14.9）：null=非多选；行首渲染勾选控件 */
  selection: ReadonlySet<number> | null;
  onImageClick: (url: string) => void;
  onStickerClick: (packId: number) => void;
  onVideoClick: (url: string) => void;
  onFileSave: (url: string, name?: string) => void;
  onReplyJump: (id: number, source?: JumpMissingSource) => void;
  /** 点头像出成员卡（21an）；仅群聊有值 */
  onAvatarTap?: ((userId: number, nickname: string) => void) | undefined;
}) {
  const { item } = props;
  if (item.type === 'date') {
    return (
      <div className={styles.dateRow} data-testid="date-row">
        <span className={styles.dateChip}>{formatDateSeparator(item.tsMs, props.nowMs)}</span>
      </div>
    );
  }
  if (item.type === 'system') return <SystemRow item={item} />;
  return (
    <MessageRow
      item={item}
      cdn={props.cdn}
      isGroup={props.isGroup}
      peerReadId={props.peerReadId}
      localUrl={props.localUrls.get(item.uuid)}
      localUrls={props.localUrls}
      flash={item.key === props.flashKey}
      selection={props.selection}
      onImageClick={props.onImageClick}
      onStickerClick={props.onStickerClick}
      onVideoClick={props.onVideoClick}
      onFileSave={props.onFileSave}
      onReplyJump={props.onReplyJump}
      onAvatarTap={props.onAvatarTap}
    />
  );
}

/** 居中提示条：文案单点映射 systemNoteText（14.5），未映射类型兜底 */
function SystemRow({ item }: { item: SystemItemVM }) {
  const label = systemNoteText(item.msgType, item.ext);
  return (
    <div className={styles.systemRow} data-testid="system-row">
      <span className={styles.systemChip}>{label !== '' ? label : '[系统消息]'}</span>
    </div>
  );
}

function MessageRow({
  item,
  cdn,
  isGroup,
  peerReadId,
  localUrl,
  localUrls,
  flash,
  selection,
  onImageClick,
  onStickerClick,
  onVideoClick,
  onFileSave,
  onReplyJump,
  onAvatarTap,
}: {
  item: MessageItemVM;
  cdn: string;
  isGroup: boolean;
  /** 对方已读到的消息 id（21v） */
  peerReadId: number;
  localUrl?: string | undefined;
  localUrls: ReadonlyMap<string, string>;
  /** 引用跳转目标的闪烁高亮（14.6b） */
  flash: boolean;
  /** 多选态（14.9）：null=非多选 */
  selection: ReadonlySet<number> | null;
  onImageClick: (url: string) => void;
  onStickerClick: (packId: number) => void;
  onVideoClick: (url: string) => void;
  onFileSave: (url: string, name?: string) => void;
  onReplyJump: (id: number, source?: JumpMissingSource) => void;
  /** 点头像（21an）：仅群聊对方消息有头像列，私聊/自己的消息恒 undefined */
  onAvatarTap?: ((userId: number, nickname: string) => void) | undefined;
}) {
  // 头像列仅群聊对方消息存在（9d 口径）；非 runEnd 行留同宽空位对齐气泡
  const hasAvatarCol = isGroup && !item.outgoing;
  // 多选勾选控件（14.9）：所有消息行渲染（不可选=置灰，旧版同口径），相册整册
  const selIds = selection !== null ? selectableIdsOf(item) : [];
  const checked = selection !== null && isItemSelected(selection, selIds);
  const rowCls = [
    styles.msgRow,
    item.outgoing ? styles.msgRowOut : '',
    item.runStart ? styles.runStart : '',
    flash ? styles.flashRow : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={rowCls}
      data-testid="msg-row"
      data-mkey={item.key}
      data-id={item.id}
      data-out={item.outgoing || undefined}
      data-send-state={item.sendState}
      data-flash={flash || undefined}
      data-selected={checked || undefined}
      data-secret={item.secret || undefined}
      data-encrypted={item.encrypted || undefined}
      data-decrypt-failed={item.decryptFailed || undefined}
    >
      {selection !== null && (
        <span
          className={[
            styles.selectCtl,
            checked ? styles.selectCtlOn : '',
            selIds.length === 0 ? styles.selectCtlDisabled : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-testid="select-ctl"
          aria-hidden="true"
        >
          {checked && <IconCheck class={styles.selectCtlGlyph} />}
        </span>
      )}
      {hasAvatarCol &&
        // 点头像出成员卡（21an，旧版 _openMessageSenderProfile 同位）。非 runEnd
        // 行只是占位空槽，仍渲染不可点的 span——那里没有头像可点。
        (item.showAvatar ? (
          <button
            type="button"
            className={`${styles.avatarSlot} ${styles.avatarButton}`}
            data-testid="msg-avatar"
            data-uid={item.senderId}
            aria-label={`${item.nickname} 的资料`}
            onClick={() => onAvatarTap?.(item.senderId, item.nickname)}
          >
            <Avatar nickname={item.nickname} avatar={item.avatar} cdn={cdn} />
          </button>
        ) : (
          <span className={styles.avatarSlot} aria-hidden="true" />
        ))}
      <div className={styles.msgCol}>
        {item.showNickname && (
          <div className={styles.nickname}>
            <span className={styles.nicknameText}>{item.nickname}</span>
            {/* 顺序照旧版 _buildSenderIdentity：昵称 → 群内身份（仅群聊）→ VIP → 靓号 */}
            {isGroup && <RoleBadge view={senderRoleBadge(item.senderRole)} />}
            <VipBadge view={userVipBadge(item.vipLevel)} />
            <PremiumBadge view={premiumNoBadge(item.premiumNo)} />
          </div>
        )}
        {item.content.kind === 'album' ? (
          <AlbumBubble
            content={item.content}
            meta={bubbleMeta(item, peerReadId, isGroup)}
            cdn={cdn}
            localUrls={localUrls}
            onImageClick={onImageClick}
          />
        ) : (
          <Bubble
            content={item.content}
            outgoing={item.outgoing}
            meta={bubbleMeta(item, peerReadId, isGroup)}
            cdn={cdn}
            rowKey={item.key}
            localUrl={localUrl}
            reply={item.replyTo}
            onImageClick={onImageClick}
            onStickerClick={onStickerClick}
            onVideoClick={onVideoClick}
            onFileSave={onFileSave}
            onReplyJump={onReplyJump}
          />
        )}
        {item.sendState === 'failed' && (
          <FailedActions clientId={item.uuid} reason={item.sendError} />
        )}
      </div>
    </div>
  );
}

/**
 * 失败气泡的重试/删除（10d）。乐观行 uuid=clientId；完整操作菜单归任务 11。
 * reason = 服务端 error_msg 原文（21f），没有就回落通用文案——旧版
 * `_handleRealtimeError` 的 `error.message.isEmpty ? '消息发送失败' : error.message`。
 */
function FailedActions({ clientId, reason }: { clientId: string; reason: string | undefined }) {
  return (
    <div className={styles.failedRow}>
      <span className={styles.failedLabel} data-testid="msg-fail-reason" title={reason}>
        {reason !== undefined && reason !== '' ? reason : '发送失败'}
      </span>
      <button
        type="button"
        className={styles.failedAction}
        data-testid="msg-retry"
        onClick={() => messagesConnection().retrySend(clientId)}
      >
        重试
      </button>
      <button
        type="button"
        className={styles.failedAction}
        data-testid="msg-discard"
        onClick={() => messagesConnection().discardSend(clientId)}
      >
        删除
      </button>
    </div>
  );
}

function bubbleMeta(
  item: MessageItemVM,
  peerReadId: number,
  isGroup: boolean,
): ComponentChildren {
  // 阅后即焚（14.13c）：meta 火焰标记（旧版 meta 行小图标同位）；端到端加密
  // （14.14b）：锁标记（解密成功与「需要密钥」占位行同标——行是密文传输）。
  // 21af 起两个都是画出来的 SVG，不再用 emoji（口径见 ui/icons.tsx 末段）。
  const base = item.edited ? `已编辑 ${item.timeLabel}` : item.timeLabel;
  const transient =
    item.sendState === 'uploading' ? '上传中…' : item.sendState === 'pending' ? '发送中…' : '';
  // 已读/已发送标记（21v）：只给自己发的私聊消息，判定口径见 chat/readState
  const status = sendStatusOf(item, peerReadId, isGroup);
  return (
    <>
      {transient === '' && item.secret && (
        <span className={styles.metaMark} data-testid="burn-mark" title="阅后即焚">
          <IconFlame class={styles.metaMarkIcon} />
        </span>
      )}
      {transient === '' && item.encrypted && (
        <span className={styles.metaMark} data-testid="enc-mark" title="端到端加密">
          <IconLock class={styles.metaMarkIcon} />
        </span>
      )}
      {transient === '' ? base : transient}
      {status !== null && <SendStatusMark status={status} />}
    </>
  );
}

/** 发送态标记（21v，旧版 _statusIcon 同字形：时钟 / 叹号 / 双勾 / 单勾） */
function SendStatusMark({ status }: { status: SendStatus }) {
  const label =
    status === 'read' ? '对方已读' : status === 'failed' ? '发送失败' : status === 'pending' ? '发送中' : '已发送';
  return (
    <span className={styles.sendMark} data-status={status} data-testid="send-status" title={label}>
      {status === 'pending' ? (
        <IconClock class={styles.sendMarkIcon} />
      ) : status === 'failed' ? (
        <IconClose class={styles.sendMarkIcon} />
      ) : status === 'read' ? (
        <IconCheckAll class={styles.sendMarkIcon} />
      ) : (
        <IconCheck class={styles.sendMarkIcon} />
      )}
    </span>
  );
}

function Avatar({ nickname, avatar, cdn }: { nickname: string; avatar: string; cdn: string }) {
  const url = resolveMediaUrl(cdn, avatar);
  return (
    <span className={styles.avatar} style={avatarStyle(nickname)}>
      <span className={styles.avatarChar}>{[...nickname][0] ?? '?'}</span>
      {url && (
        <img
          className={styles.avatarImg}
          src={url}
          alt=""
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
    </span>
  );
}

function Bubble(props: {
  /** album 走 AlbumBubble，不进本组件 */
  content: Exclude<MessageContent, { kind: 'album' }>;
  outgoing: boolean;
  meta: ComponentChildren;
  cdn: string;
  /** 行 key：语音播放的条目身份（voicePlayer 单例按 key 区分） */
  rowKey: string;
  localUrl?: string | undefined;
  /** 引用回复（14.6）：气泡内嵌引用块，点击跳转到原消息（14.6b） */
  reply?: ReplyRef | undefined;
  onImageClick: (url: string) => void;
  onStickerClick: (packId: number) => void;
  onVideoClick: (url: string) => void;
  onFileSave: (url: string, name?: string) => void;
  onReplyJump: (id: number, source?: JumpMissingSource) => void;
}) {
  const { content, outgoing, meta } = props;
  if (content.kind === 'file' || content.kind === 'video') {
    // 本地预览优先与图片同口径（14.3b 发送侧接入后自己刚发的文件用 blob URL）
    const url = props.localUrl ?? resolveMediaUrl(props.cdn, content.url);
    return (
      <FileBubble
        content={content}
        outgoing={outgoing}
        meta={meta}
        onOpen={
          url === ''
            ? undefined
            : content.kind === 'video'
              ? () => props.onVideoClick(url)
              : () => props.onFileSave(url, content.fileName)
        }
      />
    );
  }
  if (content.kind === 'contact') {
    return (
      <ContactBubble content={content} outgoing={outgoing} meta={meta} cdn={props.cdn} />
    );
  }
  if (content.kind === 'voice') {
    return (
      <VoiceBubble
        content={content}
        outgoing={outgoing}
        meta={meta}
        cdn={props.cdn}
        playKey={props.rowKey}
      />
    );
  }
  if (content.kind === 'image') {
    // 贴纸形态（14.16b）：kind=sticker 的图片兼容通道还原为无底框定宽贴纸
    // （旧版未升级客户端按普通图片渲染，新版按贴纸）。点击：pack_id>0 开整包
    // 预览（14.17c，旧版 _handleStickerMessageTap；自己/别人的同口径）；缺
    // pack_id 的异常数据回落图片预览（旧版 no-op 的小偏差——点按有响应更好）。
    const sticker = content.stickerPayload;
    const box = sticker
      ? stickerBoxSize(content.width, content.height)
      : imageBoxSize(content.width, content.height);
    // 本地预览优先（12b）：自己刚发的图用 blob URL，不碰矫正窗口期内的远端 URL
    const url = props.localUrl ?? resolveMediaUrl(props.cdn, content.url);
    return (
      <div
        className={sticker ? styles.stickerBubble : styles.imageBubble}
        style={{ width: `${box.width}px`, height: `${box.height}px` }}
        data-testid={sticker ? 'msg-sticker' : 'msg-image'}
        // 点图全屏预览（12c）；长按路径已被 useLongPress 抑制 click，不冲突
        onClick={
          url !== ''
            ? () => {
                if (sticker && content.stickerPackId > 0)
                  props.onStickerClick(content.stickerPackId);
                else props.onImageClick(url);
              }
            : undefined
        }
      >
        {url && (
          <img
            className={sticker ? styles.stickerImage : styles.image}
            src={url}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <span className={styles.imageMeta}>{meta}</span>
      </div>
    );
  }
  const cls = [
    styles.bubble,
    outgoing ? styles.bubbleOut : styles.bubbleIn,
    content.kind === 'placeholder' ? styles.bubblePlaceholder : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      {props.reply !== undefined && (
        <ReplyBlock reply={props.reply} cdn={props.cdn} onJump={props.onReplyJump} />
      )}
      <span className={styles.bubbleText}>
        {content.kind === 'text' ? (
          <RichText text={content.text} mentionTargets={content.mentionTargets} />
        ) : (
          content.label
        )}
      </span>
      <span className={styles.meta}>{meta}</span>
    </div>
  );
}

/**
 * 气泡正文的富文本渲染（21l，旧版 message_bubble.dart:2077~2175 同构）：
 * @提及与链接可点。切分口径（含「先切链接再切提及」「结尾标点吐回正文」
 * 「协议白名单」）全在 `~/chat/richText`，这里只负责渲染与点击。
 *
 * - **提及**：`userId>0` 才渲染成按钮进资料页；对不上目标的只高亮不可点
 *   （旧版 recognizer 为 null 同义）。旧版点 @ 是进群成员资料，新版复用
 *   既有的好友详情页（`friend/info` 对非好友也返回昵称等字段）。
 * - **链接**：`target="_blank"` + `rel="noopener noreferrer"`。前者在 iOS
 *   standalone 下正好满足 PROMPT「外部链接必须在 Safari 打开，别让 Web Clip
 *   跳出去回不来」；后者防 `window.opener` 劫持。
 * - 点击要 `stopPropagation`：气泡本身挂着长按/多选等手势。
 */
function RichText({
  text,
  mentionTargets,
}: {
  text: string;
  mentionTargets: readonly number[];
}) {
  const segments = useMemo(() => parseRichText(text, mentionTargets), [text, mentionTargets]);
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.kind === 'link') {
          return (
            <a
              key={i}
              className={styles.richLink}
              href={seg.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="msg-link"
              onClick={(e) => e.stopPropagation()}
            >
              {seg.text}
            </a>
          );
        }
        if (seg.kind === 'mention') {
          if (seg.userId <= 0) {
            return (
              <span key={i} className={styles.richMention} data-testid="msg-mention">
                {seg.text}
              </span>
            );
          }
          return (
            <button
              key={i}
              type="button"
              className={styles.richMention}
              data-testid="msg-mention"
              data-uid={seg.userId}
              onClick={(e) => {
                e.stopPropagation();
                openFriendDetail(appRouter(), seg.userId);
              }}
            >
              {seg.text}
            </button>
          );
        }
        return <span key={i}>{seg.text}</span>;
      })}
    </>
  );
}

/**
 * 气泡内嵌引用块（14.6，旧版 _buildInlineReplyPreview 同构）：竖线 + 发送者名
 * （空兜底「原消息」）+ 快照文本两行省略（空兜底「(空)」）。点击跳转到原消息
 * +闪烁高亮（14.6b；id=0 的旧数据无从定位，不挂点击）。
 *
 * 21ah-2：ext 里带了原文快照的（新发的消息，见 replyModel）多显一张缩略图——
 * 引用图片不再只是一行「[图片]」；点击跳不过去时把整条原消息弹窗原样摊开。
 */
function ReplyBlock({
  reply,
  cdn,
  onJump,
}: {
  reply: ReplyRef;
  cdn: string;
  onJump: (id: number, source?: JumpMissingSource) => void;
}) {
  const thumb = resolveMediaUrl(cdn, replyThumbPath(reply));
  return (
    <span
      // 21c-2：两侧气泡都是浅底，引用块不再按侧分叉
      className={styles.replyBlock}
      data-testid="msg-reply"
      data-reply-id={reply.id}
      // 跳不过去时拿这份快照直接弹窗（21ah）：新消息带正文 envelope → 原样气泡；
      // 老消息只有 text → 弹窗显那行字
      onClick={
        reply.id > 0
          ? () =>
              onJump(reply.id, {
                name: reply.name,
                msgType: reply.msgType,
                content: reply.srcMsg,
                text: reply.text,
              })
          : undefined
      }
    >
      {thumb !== '' && (
        <img
          className={styles.replyThumb}
          src={thumb}
          alt=""
          loading="lazy"
          data-testid="msg-reply-thumb"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <span className={styles.replyBody}>
        <span className={styles.replyName}>{reply.name !== '' ? reply.name : '原消息'}</span>
        <span className={styles.replyText}>{reply.text !== '' ? reply.text : '(空)'}</span>
      </span>
    </span>
  );
}

/**
 * 语音气泡（14.2a）：播放/暂停圆钮 + 24 根装饰波形条（协议无真实波形数据，
 * 旧版同样走固定高度表）+ 时长（播放中显示进度位置，legacy 同口径）。
 * 点整个气泡切换播放；播放态来自 voicePlayer 单例（同一时刻只播一条）。
 * 固定宽度 210px——虚拟列表测高稳定，与旧版一致。
 */
function VoiceBubble(props: {
  content: Extract<MessageContent, { kind: 'voice' }>;
  outgoing: boolean;
  meta: ComponentChildren;
  cdn: string;
  playKey: string;
}) {
  const st = useVoiceEntryState(props.playKey);
  const url = resolveMediaUrl(props.cdn, props.content.url);
  const active = st.key === props.playKey;
  const playing = active && st.status === 'playing';
  const failed = active && st.status === 'error';
  // 时长以媒体元数据为准（播放后拿到），未知回落 envelope dur_ms
  const totalMs = st.durationMs > 0 ? st.durationMs : props.content.durationMs;
  const progress =
    active && totalMs > 0 ? Math.min(Math.max(st.positionMs / totalMs, 0), 1) : 0;
  const label = failed
    ? '播放失败'
    : formatVoiceDuration(playing && st.positionMs > 0 ? st.positionMs : totalMs);
  const cls = [
    styles.bubble,
    props.outgoing ? styles.bubbleOut : styles.bubbleIn,
    styles.voiceBubble,
  ].join(' ');
  return (
    <div
      className={cls}
      data-testid="msg-voice"
      data-state={active ? st.status : 'idle'}
      onClick={() => voicePlayer().toggle(props.playKey, url)}
    >
      <span className={styles.voicePlay} aria-hidden="true">
        {playing ? (
          <svg viewBox="0 0 24 24">
            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </span>
      <span className={styles.voiceBody}>
        <span className={styles.voiceBars}>
          {Array.from({ length: VOICE_BAR_COUNT }, (_, i) => (
            <span
              key={i}
              className={
                active && i / (VOICE_BAR_COUNT - 1) <= progress
                  ? `${styles.voiceBar} ${styles.voiceBarOn}`
                  : styles.voiceBar
              }
              style={{ height: `${voiceBarHeight(i)}px` }}
            />
          ))}
        </span>
        <span className={styles.voiceMetaRow}>
          <span className={styles.voiceDur} data-testid="voice-duration">
            {label}
          </span>
          <span className={styles.meta}>{props.meta}</span>
        </span>
      </span>
    </div>
  );
}

/**
 * 文件/视频通用卡片（14.3，两类同构——旧版视频同样渲染为文件卡片无缩略图）：
 * 图标（文件=文档页 / 视频=播放三角）+ 文件名单行省略 + 大小（视频另带时长）。
 * 点整卡：文件=下载/另存为（PC download、H5/standalone share，saveAs.ts 口径）、
 * 视频=打开播放浮层。固定宽度（虚拟列表测高稳定，语音气泡同策）。
 */
function FileBubble(props: {
  content: Extract<MessageContent, { kind: 'file' } | { kind: 'video' }>;
  outgoing: boolean;
  meta: ComponentChildren;
  /** undefined = url 为空（坏 envelope），卡片只展示不可点 */
  onOpen?: (() => void) | undefined;
}) {
  const c = props.content;
  const isVideo = c.kind === 'video';
  const name = c.fileName !== '' ? c.fileName : isVideo ? '视频' : '文件';
  const sizeLabel = formatFileSize(c.size);
  const info = [
    ...(isVideo && c.durationMs > 0 ? [formatVoiceDuration(c.durationMs)] : []),
    ...(sizeLabel !== '' ? [sizeLabel] : []),
  ].join(' · ');
  const cls = [
    styles.bubble,
    props.outgoing ? styles.bubbleOut : styles.bubbleIn,
    styles.fileBubble,
  ].join(' ');
  return (
    <div
      className={cls}
      style={{ width: `${FILE_CARD_WIDTH}px` }}
      data-testid={isVideo ? 'msg-video' : 'msg-file'}
      onClick={props.onOpen}
    >
      <span className={styles.fileIcon} aria-hidden="true">
        {isVideo ? (
          <svg viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9z" />
          </svg>
        )}
      </span>
      <span className={styles.fileBody}>
        <span className={styles.fileName} data-testid="file-name">
          {name}
        </span>
        <span className={styles.fileMetaRow}>
          <span className={styles.fileInfo} data-testid="file-info">
            {info}
          </span>
          <span className={styles.meta}>{props.meta}</span>
        </span>
      </span>
    </div>
  );
}

/**
 * 名片卡片（14.4）：头像+名字+ID 行、分隔线、底标「联系人名片」——旧版
 * _buildContactCardContent 同构（名字空兜底「联系人名片」、username 空显
 * 「个人名片」）。点击→好友详情/加好友归 14.21，详情页接入前卡片不可点。
 * 固定宽度（虚拟列表测高稳定，文件卡片同策）。
 */
function ContactBubble(props: {
  content: Extract<MessageContent, { kind: 'contact' }>;
  outgoing: boolean;
  meta: ComponentChildren;
  cdn: string;
}) {
  const c = props.content;
  const name = c.name !== '' ? c.name : '联系人名片';
  const cls = [
    styles.bubble,
    props.outgoing ? styles.bubbleOut : styles.bubbleIn,
    styles.contactBubble,
  ].join(' ');
  // 点击进被分享人的好友详情（21ai 补 14.4 的欠账）。
  // 14.4 当时写「详情页接入前卡片不可点」，而 14.21 的好友详情页早已全部完成，
  // 这一下接线一直没补上——人工报「群名片点击没反应」。
  // userId=0（envelope 缺字段）时仍不可点：没有目标可跳，点了只会进一个空详情页。
  const canOpen = c.userId > 0;
  return (
    <div
      className={cls}
      style={{ width: `${FILE_CARD_WIDTH}px` }}
      data-testid="msg-contact"
      data-openable={canOpen ? 'true' : undefined}
      onClick={canOpen ? () => openFriendDetail(appRouter(), c.userId) : undefined}
    >
      <span className={styles.contactHead}>
        <span className={styles.contactAvatar}>
          <Avatar nickname={name} avatar={c.avatar} cdn={props.cdn} />
        </span>
        <span className={styles.contactIdent}>
          <span className={styles.contactName} data-testid="contact-name">
            {name}
          </span>
          <span className={styles.contactId} data-testid="contact-id">
            {c.username !== '' ? `ID: ${c.username}` : '个人名片'}
          </span>
        </span>
      </span>
      <span className={styles.contactFooter}>
        <span>联系人名片</span>
        <span className={styles.meta}>{props.meta}</span>
      </span>
    </div>
  );
}

/**
 * 相册网格气泡（14.1）：绝对定位 tile，布局=albumModel.computeAlbumLayout。
 * 每张 tile 自带 data-mkey（菜单按张）、点击按张开预览；本地预览 URL 按张
 * uuid 查（12b 口径——自己刚发的张不碰远端矫正窗口期）。
 */
function AlbumBubble(props: {
  content: Extract<MessageContent, { kind: 'album' }>;
  meta: ComponentChildren;
  cdn: string;
  localUrls: ReadonlyMap<string, string>;
  onImageClick: (url: string) => void;
}) {
  const { images } = props.content;
  const layout = computeAlbumLayout(images, { maxWidth: IMAGE_MAX_W });
  return (
    <div
      className={styles.albumBubble}
      style={{ width: `${layout.width}px`, height: `${layout.height}px` }}
      data-testid="msg-album"
      data-count={images.length}
    >
      {images.map((img, i) => {
        const t = layout.tiles[i]!;
        const url = props.localUrls.get(img.uuid) ?? resolveMediaUrl(props.cdn, img.url);
        return (
          <div
            key={img.key}
            className={styles.albumTile}
            data-mkey={img.key}
            data-testid="album-tile"
            style={{
              left: `${t.left}px`,
              top: `${t.top}px`,
              width: `${t.width}px`,
              height: `${t.height}px`,
            }}
            onClick={url !== '' ? () => props.onImageClick(url) : undefined}
          >
            {url && (
              <img
                className={styles.image}
                src={url}
                alt=""
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </div>
        );
      })}
      <span className={styles.imageMeta}>{props.meta}</span>
    </div>
  );
}
